//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import ExtensionIcon from '@mui/icons-material/Extension';

import { type PluginSurfaceRow } from './pluginSurfaces';
import { type ToolEntry } from './toolEntries';
import {
  panelIdForRow, toPanelSpec
} from '@/components/Panels/pluginPanelSpec';

/**
 * The launcher entry for one plugin surface.
 *
 * A panel surface is named and drawn from the very spec the registry stores,
 * so its rail pill, its Tools row and the panel's own title bar cannot
 * disagree about what it is. The spec's `render` belongs to the registrar and
 * nothing read here touches it, which is why a placeholder is handed in.
 *
 * A modal surface has no spec -- it is launchable and never registered -- so
 * it is named by its menu label and wears the plugin glyph, which identifies
 * it no better than it identifies any other surface.
 */
export const pluginToolEntry = (
  row: PluginSurfaceRow, onOpen: (row: PluginSurfaceRow) => void
): ToolEntry => {
  const panelId = panelIdForRow(row);
  const spec = panelId === null ? null : toPanelSpec(row, () => null);
  return {
    // A modal surface has no panel id to be pinned under, so one is
    // synthesized from the same pair, neither half of which is unique alone.
    id: panelId ?? `surface:${row.plugin.name}:${row.key}`,
    label: spec?.title ?? row.surface.label,
    name: row.surface.label,
    icon: spec?.icon ?? <ExtensionIcon fontSize="inherit" />,
    isIconGeneric: spec?.isIconGeneric ?? true,
    panelId: panelId ?? undefined,
    open: () => onOpen(row)
  };
};
